(() => {
  if (window.__reploIntelligemsPriceSyncInitialized) return;
  window.__reploIntelligemsPriceSyncInitialized = true;

  const CONTAINER_SELECTOR = '[data-replo-product-container]';
  const POLL_INTERVAL_MS = 250;
  const INTELLIGEMS_RETRY_DELAY_MS = 150;
  const INTELLIGEMS_MAX_ATTEMPTS = 60;

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    const amount = (cents / 100).toFixed(precision);
    const parts = amount.split('.');
    const dollarsAmount = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const centsAmount = parts[1] ? `${decimal}${parts[1]}` : '';
    return `${dollarsAmount}${centsAmount}`;
  }

  function getMoneyFormat() {
    const themeMoneyFormat = window.themeVariables?.settings?.moneyFormat;
    if (themeMoneyFormat) return themeMoneyFormat;

    const reploStoreData = document.getElementById('replo-deps-shopify-store');
    if (reploStoreData?.textContent) {
      try {
        const parsed = JSON.parse(reploStoreData.textContent);
        if (parsed?.shop?.moneyFormat) {
          return parsed.shop.moneyFormat;
        }
      } catch (error) {
        console.warn('Replo IntelliGems: Failed to parse Replo money format', error);
      }
    }

    return '${{amount}}';
  }

  function formatMoney(cents, format = getMoneyFormat()) {
    if (cents == null || Number.isNaN(Number(cents))) return '';

    const normalizedCents = Math.round(Number(cents));
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const placeholder = format.match(placeholderRegex)?.[1] ?? 'amount';

    let formattedAmount = '';
    switch (placeholder) {
      case 'amount':
        formattedAmount = formatWithDelimiters(normalizedCents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        formattedAmount = formatWithDelimiters(normalizedCents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formattedAmount = formatWithDelimiters(normalizedCents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedAmount = formatWithDelimiters(normalizedCents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        formattedAmount = formatWithDelimiters(normalizedCents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        formattedAmount = formatWithDelimiters(normalizedCents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formattedAmount = formatWithDelimiters(normalizedCents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_apostrophe_separator':
        formattedAmount = formatWithDelimiters(normalizedCents, 0, "'", '.');
        break;
      default:
        formattedAmount = formatWithDelimiters(normalizedCents, 2, ',', '.');
        break;
    }

    return format.replace(placeholderRegex, formattedAmount);
  }

  function parsePriceStringToCents(value) {
    if (value == null || value === '') return null;

    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.round(value * 100);
    }

    let normalizedValue = String(value).trim().replace(/[^\d,.-]/g, '');
    if (!normalizedValue) return null;

    const hasComma = normalizedValue.includes(',');
    const hasDot = normalizedValue.includes('.');

    if (hasComma && hasDot) {
      if (normalizedValue.lastIndexOf(',') > normalizedValue.lastIndexOf('.')) {
        normalizedValue = normalizedValue.replace(/\./g, '').replace(',', '.');
      } else {
        normalizedValue = normalizedValue.replace(/,/g, '');
      }
    } else if (hasComma) {
      normalizedValue = normalizedValue.replace(',', '.');
    }

    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? Math.round(parsedValue * 100) : null;
  }

  function hasIntelligemsPriceApi() {
    return Boolean(
      window.igData?.price?.getPriceByVariantId
      && window.igData?.price?.getComparePriceByVariantId
    );
  }

  function getIntelligemsPriceCents(variantId) {
    if (!hasIntelligemsPriceApi() || !variantId) return null;

    try {
      return parsePriceStringToCents(window.igData.price.getPriceByVariantId(String(variantId)));
    } catch (error) {
      console.warn('Replo IntelliGems: Failed to read tested price', error);
      return null;
    }
  }

  function getIntelligemsComparePriceCents(variantId) {
    if (!hasIntelligemsPriceApi() || !variantId) return null;

    try {
      return parsePriceStringToCents(window.igData.price.getComparePriceByVariantId(String(variantId)));
    } catch (error) {
      console.warn('Replo IntelliGems: Failed to read tested compare price', error);
      return null;
    }
  }

  function setDisplayText(element, text) {
    if (!element) return;

    const childElement = element.children.length === 1 ? element.firstElementChild : null;
    if (childElement && element.childNodes.length === 1) {
      setDisplayText(childElement, text);
      return;
    }

    const nonEmptyTextNodes = Array.from(element.childNodes).filter((node) => (
      node.nodeType === Node.TEXT_NODE && node.nodeValue.trim() !== ''
    ));

    if (nonEmptyTextNodes.length === 1 && element.childNodes.length === 1) {
      nonEmptyTextNodes[0].nodeValue = text;
      return;
    }

    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        return node.nodeValue.trim() !== ''
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP;
      }
    });

    const firstTextNode = walker.nextNode();
    const secondTextNode = walker.nextNode();
    if (firstTextNode && !secondTextNode) {
      firstTextNode.nodeValue = text;
      return;
    }

    element.textContent = text;
  }

  function formatSavingsDisplayText(element, formattedSavings) {
    const currentText = element?.textContent?.replace(/\s+/g, ' ').trim() || '';

    if (/du sparst/i.test(currentText)) {
      return `Du sparst ${formattedSavings}`;
    }

    return formattedSavings;
  }

  class ReploIntelligemsPriceSync {
    constructor(container) {
      this.container = container;
      this.bundleConfig = this.#getBundleConfig();
      this.productId = this.#getProductId();
      this.lastVariantId = '';
      this.lastSelectedOption = '';
      this.lastNodeSignature = '';
      this.lastIntelligemsReady = hasIntelligemsPriceApi();

      this.sync = this.sync.bind(this);
      this.syncIfNeeded = this.syncIfNeeded.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    init() {
      this.sync();
      this.#scheduleIntelligemsRefresh();
      this.#startObservers();
    }

    syncIfNeeded(force = false) {
      if (!document.body.contains(this.container)) return;

      const currentVariantId = this.#getVariantId();
      const currentSelectedOption = this.#getSelectedOption();
      const currentNodeSignature = this.#getNodeSignature();
      const intelligemsReady = hasIntelligemsPriceApi();

      if (
        !force
        && currentVariantId === this.lastVariantId
        && currentSelectedOption === this.lastSelectedOption
        && currentNodeSignature === this.lastNodeSignature
        && intelligemsReady === this.lastIntelligemsReady
      ) {
        return;
      }

      this.sync();
    }

    sync() {
      if (!document.body.contains(this.container)) return;

      const variantId = this.#getVariantId();
      if (!variantId) return;

      const selectedOption = this.#getSelectedOption();
      const singlePricingState = this.#resolveSinglePricingState(variantId);
      const pricingState = this.#resolveDisplayPricingState(selectedOption, singlePricingState);
      const priceElements = this.#getElements('[data-replo-price]');
      const compareElements = this.#getElements('[data-replo-compare-price]');
      const savingsElements = this.#getElements('[data-replo-compare-difference]');
      const percentageElements = this.#getElements('[data-replo-compare-percentage]');

      if (!priceElements.length && !compareElements.length && !savingsElements.length && !percentageElements.length) {
        return;
      }

      const metadataTargets = [
        ...priceElements,
        ...compareElements,
        ...savingsElements,
        ...percentageElements
      ];

      metadataTargets.forEach((element) => this.#updateElementMetadata(element, variantId));

      if (pricingState) {
        const formattedPrice = formatMoney(pricingState.priceCents);
        const formattedComparePrice = formatMoney(pricingState.comparePriceCents);
        const formattedSavings = formatMoney(pricingState.savingsCents);
        const percentageLabel = `${pricingState.savingsPercent}%`;
        const showCompare = pricingState.comparePriceCents > pricingState.priceCents;

        priceElements.forEach((element) => {
          element.setAttribute('data-replo-price', formattedPrice);
          setDisplayText(element, formattedPrice);
        });

        compareElements.forEach((element) => {
          element.setAttribute('data-replo-compare-price', formattedComparePrice);
          element.style.display = showCompare ? '' : 'none';
          if (showCompare) {
            setDisplayText(element, formattedComparePrice);
          }
        });

        savingsElements.forEach((element) => {
          element.setAttribute('data-replo-compare-difference', formattedSavings);
          element.style.display = showCompare ? '' : 'none';
          if (showCompare) {
            setDisplayText(element, formatSavingsDisplayText(element, formattedSavings));
          }
        });

        percentageElements.forEach((element) => {
          element.setAttribute('data-replo-compare-percentage', percentageLabel);
          element.style.display = showCompare ? '' : 'none';
          if (showCompare) {
            setDisplayText(element, `(${percentageLabel})`);
          }
        });
      }

      this.lastVariantId = variantId;
      this.lastSelectedOption = selectedOption;
      this.lastNodeSignature = this.#getNodeSignature();
      this.lastIntelligemsReady = hasIntelligemsPriceApi();
    }

    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.syncIfNeeded(true);
      }
    }

    #getElements(selector) {
      return Array.from(this.container.querySelectorAll(selector));
    }

    #getProductId() {
      return String(
        this.bundleConfig?.productId
        || this.container.getAttribute('data-replo-product-container')
        || this.container.querySelector('form')?.getAttribute('data-productid')
        || this.container.querySelector('[data-product-id]')?.getAttribute('data-product-id')
        || ''
      );
    }

    #getVariantId() {
      const bundleRoot = this.container.querySelector('[data-single-dual-bundle]');
      const selectedOption = this.#getSelectedOption();

      if (selectedOption === 'single') {
        const selectedTypeRadio = bundleRoot?.querySelector('[data-type-radio="1"]:checked');
        if (selectedTypeRadio?.value) {
          return String(selectedTypeRadio.value);
        }
      } else {
        const firstBundleVariantRadio = bundleRoot?.querySelector('[data-variant-radio="2"]:checked');
        if (firstBundleVariantRadio?.value) {
          return String(firstBundleVariantRadio.value);
        }
      }

      const variantInput = this.container.querySelector('product-form input[name="id"], form input[name="id"]');
      if (variantInput?.value) {
        return String(variantInput.value);
      }

      const variantElement = this.container.querySelector('[data-variant-id]');
      return variantElement ? String(variantElement.getAttribute('data-variant-id') || '') : '';
    }

    #getSelectedOption() {
      const selectedOption = this.container.querySelector('[data-option-radio]:checked')?.value;
      return selectedOption || 'single';
    }

    #getBundleConfig() {
      const configElement = this.container.querySelector('[data-bundle-config]');
      if (!configElement?.textContent) return null;

      try {
        return JSON.parse(configElement.textContent);
      } catch (error) {
        console.warn('Replo IntelliGems: Failed to parse bundle config', error);
        return null;
      }
    }

    #getNodeSignature() {
      const selectors = [
        '[data-replo-price]',
        '[data-replo-compare-price]',
        '[data-replo-compare-difference]',
        '[data-replo-compare-percentage]'
      ];

      return selectors.map((selector) => this.container.querySelectorAll(selector).length).join(':');
    }

    #updateElementMetadata(element, variantId) {
      if (this.productId) {
        element.setAttribute('data-product-id', this.productId);
      }

      if (variantId) {
        element.setAttribute('data-variant-id', variantId);
      }
    }

    #resolveSinglePricingState(variantId) {
      const variant = this.#getVariantById(variantId);
      const pricingState = {
        priceCents: Number(this.bundleConfig?.singlePrice || 0),
        comparePriceCents: Number(this.bundleConfig?.singleComparePrice || 0),
        savingsCents: Math.max(Number(this.bundleConfig?.singleComparePrice || 0) - Number(this.bundleConfig?.singlePrice || 0), 0),
        savingsPercent: Number(this.bundleConfig?.singleDiscountBadge || 0),
        usingIntelligems: false,
        usingVariantPriceFallback: false
      };

      const variantPriceCents = this.#getVariantPriceCents(variant);
      const variantComparePriceCents = this.#getVariantComparePriceCents(variant);

      if (pricingState.priceCents <= 0 && variantPriceCents > 0) {
        pricingState.priceCents = variantPriceCents;
        pricingState.comparePriceCents = variantComparePriceCents;
        pricingState.savingsCents = Math.max(pricingState.comparePriceCents - pricingState.priceCents, 0);
        pricingState.savingsPercent = pricingState.comparePriceCents > pricingState.priceCents
          ? Math.round((pricingState.savingsCents * 100) / pricingState.comparePriceCents)
          : 0;
        pricingState.usingVariantPriceFallback = true;
      }

      const priceCents = getIntelligemsPriceCents(variantId);
      if (priceCents == null) {
        const hasFallbackPrice = Number(pricingState.priceCents || 0) > 0 || Number(pricingState.comparePriceCents || 0) > 0;
        return hasFallbackPrice ? pricingState : null;
      }

      const comparePriceCents = getIntelligemsComparePriceCents(variantId) ?? 0;
      pricingState.priceCents = priceCents;
      pricingState.comparePriceCents = comparePriceCents;
      pricingState.savingsCents = Math.max(comparePriceCents - priceCents, 0);
      pricingState.savingsPercent = comparePriceCents > priceCents
        ? Math.round((pricingState.savingsCents * 100) / comparePriceCents)
        : 0;
      pricingState.usingIntelligems = true;
      pricingState.usingVariantPriceFallback = false;

      return pricingState;
    }

    #resolveDisplayPricingState(selectedOption, singlePricingState) {
      const publishedPricingState = this.#getPublishedPricingState(selectedOption);
      if (publishedPricingState) {
        return publishedPricingState;
      }

      if (!singlePricingState) return null;

      if (selectedOption === 'dual') {
        return this.#normalizeBundlePricingState(this.#resolveBundlePricing(singlePricingState, 2));
      }

      if (selectedOption === 'trio') {
        return this.#normalizeBundlePricingState(this.#resolveBundlePricing(singlePricingState, 3));
      }

      return singlePricingState;
    }

    #getPublishedPricingState(selectedOption) {
      const bundleRoot = this.container.querySelector('[data-single-dual-bundle]');
      const optionKey = selectedOption === 'dual' || selectedOption === 'trio' ? selectedOption : 'single';
      if (!bundleRoot) return null;

      const productId = String(bundleRoot.getAttribute(`data-pricing-${optionKey}-product-id`) || '');
      const variantId = String(bundleRoot.getAttribute(`data-pricing-${optionKey}-variant-id`) || '');
      const totalPriceCents = Number(bundleRoot.getAttribute(`data-pricing-${optionKey}-total-price-cents`));
      const totalComparePriceCents = Number(bundleRoot.getAttribute(`data-pricing-${optionKey}-total-compare-price-cents`));
      const pricePerItemCents = Number(bundleRoot.getAttribute(`data-pricing-${optionKey}-price-per-item-cents`));
      const comparePricePerItemCents = Number(bundleRoot.getAttribute(`data-pricing-${optionKey}-compare-price-per-item-cents`));
      const savingsPercent = Number(bundleRoot.getAttribute(`data-pricing-${optionKey}-savings-percent`));

      if (!Number.isFinite(pricePerItemCents) || pricePerItemCents <= 0) {
        return null;
      }

      const comparePriceCents = Number.isFinite(comparePricePerItemCents) ? comparePricePerItemCents : 0;
      return {
        productId,
        variantId,
        totalPriceCents: Number.isFinite(totalPriceCents) ? totalPriceCents : pricePerItemCents,
        totalComparePriceCents: Number.isFinite(totalComparePriceCents) ? totalComparePriceCents : comparePriceCents,
        priceCents: pricePerItemCents,
        comparePriceCents,
        savingsCents: Math.max(comparePriceCents - pricePerItemCents, 0),
        savingsPercent: Number.isFinite(savingsPercent) ? savingsPercent : 0,
        usingIntelligems: false,
        usingPublishedPricing: true
      };
    }

    #resolveBundlePricing(singlePricingState, quantity) {
      const bundleKey = quantity === 2 ? 'duo' : 'trio';
      const totalPriceCents = Number(this.bundleConfig?.[`${bundleKey}Price`] || 0);
      const totalComparePriceCents = Number(this.bundleConfig?.[`${bundleKey}ComparePrice`] || 0);
      const fallbackDiscountBadge = Number(this.bundleConfig?.[`${bundleKey}DiscountBadge`] || 0);
      const testingDiscountCents = Number(this.bundleConfig?.[quantity === 2 ? 'testingDuoDiscount' : 'testingTrioDiscount'] || 0);
      const pricingState = {
        totalPriceCents,
        totalComparePriceCents,
        pricePerItemCents: Math.round(totalPriceCents / quantity),
        comparePricePerItemCents: Math.round(totalComparePriceCents / quantity),
        savingsPercent: fallbackDiscountBadge,
        usingPriceTesting: false
      };

      const canUseAutomaticPricing = Boolean(
        this.bundleConfig?.enablePriceTestingMode
        && Number(singlePricingState?.priceCents || 0) > 0
        && (singlePricingState?.usingIntelligems || singlePricingState?.usingVariantPriceFallback)
      );

      if (!canUseAutomaticPricing) {
        return pricingState;
      }

      const liveBundleTotalCents = Math.max((Number(singlePricingState.priceCents || 0) * quantity) - testingDiscountCents, 0);
      const liveBundleCompareTotalCents = Math.max(Number(singlePricingState.comparePriceCents || 0) * quantity, 0);
      const savingsCents = Math.max(liveBundleCompareTotalCents - liveBundleTotalCents, 0);

      pricingState.totalPriceCents = liveBundleTotalCents;
      pricingState.totalComparePriceCents = liveBundleCompareTotalCents;
      pricingState.pricePerItemCents = Math.round(liveBundleTotalCents / quantity);
      pricingState.comparePricePerItemCents = Math.round(liveBundleCompareTotalCents / quantity);
      pricingState.savingsPercent = liveBundleCompareTotalCents > liveBundleTotalCents
        ? Math.round((savingsCents * 100) / liveBundleCompareTotalCents)
        : 0;
      pricingState.usingPriceTesting = Boolean(singlePricingState?.usingIntelligems);

      return pricingState;
    }

    #normalizeBundlePricingState(pricingState) {
      if (!pricingState) return null;

      const priceCents = Number(pricingState.pricePerItemCents || 0);
      const comparePriceCents = Number(pricingState.comparePricePerItemCents || 0);
      const savingsCents = Math.max(comparePriceCents - priceCents, 0);

      return {
        priceCents,
        comparePriceCents,
        savingsCents,
        savingsPercent: Number(pricingState.savingsPercent || 0),
        usingIntelligems: Boolean(pricingState.usingPriceTesting)
      };
    }

    #getVariantById(variantId) {
      if (!variantId) return null;
      return this.bundleConfig?.variants?.find((variant) => String(variant.id) === String(variantId)) || null;
    }

    #getVariantPriceCents(variant) {
      const priceCents = Number(variant?.price);
      return Number.isFinite(priceCents) && priceCents > 0 ? priceCents : 0;
    }

    #getVariantComparePriceCents(variant) {
      const comparePriceCents = Number(variant?.compare_at_price);
      return Number.isFinite(comparePriceCents) && comparePriceCents > 0 ? comparePriceCents : 0;
    }

    #hasResolvedIntelligemsPricing(variantId) {
      const priceCents = getIntelligemsPriceCents(variantId);
      if (!(Number.isFinite(Number(priceCents)) && Number(priceCents) > 0)) {
        return false;
      }

      const expectsComparePrice = Number(this.bundleConfig?.singleComparePrice || 0) > 0;
      if (!expectsComparePrice) {
        return true;
      }

      return getIntelligemsComparePriceCents(variantId) != null;
    }

    #scheduleIntelligemsRefresh() {
      const initialVariantId = this.#getVariantId();
      if (initialVariantId && this.#hasResolvedIntelligemsPricing(initialVariantId)) {
        this.syncIfNeeded(true);
        return;
      }

      let attempts = 0;

      const tryRefresh = () => {
        if (!document.body.contains(this.container)) return;

        const activeVariantId = this.#getVariantId();
        if (activeVariantId && this.#hasResolvedIntelligemsPricing(activeVariantId)) {
          this.syncIfNeeded(true);
          return;
        }

        attempts += 1;
        if (attempts >= INTELLIGEMS_MAX_ATTEMPTS) return;

        window.setTimeout(tryRefresh, INTELLIGEMS_RETRY_DELAY_MS);
      };

      window.setTimeout(tryRefresh, 0);
      window.addEventListener('load', tryRefresh, { once: true });
    }

    #startObservers() {
      this.container.addEventListener('change', this.syncIfNeeded, true);
      this.container.addEventListener('input', this.syncIfNeeded, true);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('pageshow', this.syncIfNeeded);

      this.mutationObserver = new MutationObserver(() => this.syncIfNeeded());
      this.mutationObserver.observe(this.container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value', 'data-variant-id', 'data-product-id']
      });

      this.pollInterval = window.setInterval(this.syncIfNeeded, POLL_INTERVAL_MS);
    }
  }

  function initReploIntelligemsPriceSync() {
    document.querySelectorAll(CONTAINER_SELECTOR).forEach((container) => {
      if (container.dataset.reploIntelligemsSyncInitialized === 'true') return;

      container.dataset.reploIntelligemsSyncInitialized = 'true';
      new ReploIntelligemsPriceSync(container).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReploIntelligemsPriceSync, { once: true });
  } else {
    initReploIntelligemsPriceSync();
  }

  window.addEventListener('load', initReploIntelligemsPriceSync);
})();
